import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { FiUser, FiSmartphone, FiCreditCard, FiGift, FiStar } from 'react-icons/fi';
import './PaymentUtilities.scss';

// --- CUSTOM NODES ---

const CreditCardNode = ({ data }) => {
  return (
    <div className="pu-card-wrapper">
      {data.isCenter && data.bottomText && (
        <div className="card-label label-top">
          {data.bottomText.split('\n').map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}
      
      <div className={`pu-card-node ${data.variant} ${data.isCenter ? 'center-card' : ''}`}>
        <Handle type="target" position={Position.Left} id="left" style={{ opacity: 0 }} />
        <Handle type="source" position={Position.Right} id="right" style={{ opacity: 0 }} />
        <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
        <Handle type="target" position={Position.Top} id="top" style={{ opacity: 0 }} />
        
        <div className="card-body">
          <div className="card-top">
            <FiStar /> <span>{data.subtitle}</span>
          </div>
          <div className="card-center">{data.title}</div>
          <div className="card-bottom">
            <span className="card-num">**** **** **** 3456</span>
            {data.variant === 'gold' && <span className="visa-logo">VISA</span>}
          </div>
        </div>
      </div>

      {!data.isCenter && data.bottomText && (
        <div className="card-label label-bottom">
          {data.bottomText.split('\n').map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}
    </div>
  );
};

const IconNode = ({ data }) => {
  const getIcon = () => {
    if (data.icon === 'user') return (
      <div className="icon-wrap user-wrap">
        <FiSmartphone className="sub-icon" />
        <FiUser className="main-icon" />
      </div>
    );
    if (data.icon === 'wallet') return <div className="icon-wrap wallet-wrap"><FiCreditCard className="main-icon" /></div>;
    if (data.icon === 'points') return <div className="icon-wrap points-wrap"><FiGift className="main-icon" /></div>;
  };

  return (
    <div className="pu-icon-node">
      <Handle type="target" position={Position.Top} id="top" style={{ top: '0', opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ bottom: '0', opacity: 0 }} />
      
      {/* Align Left/Right handles exactly to the center of the 80x80 circle */}
      <Handle type="target" position={Position.Left} id="left" style={{ top: '40px', left: '20px', opacity: 0 }} />
      <Handle type="source" position={Position.Right} id="right" style={{ top: '40px', right: '20px', opacity: 0 }} />

      {getIcon()}
      <div className="icon-label">
         {data.label && data.label.split('\n').map((line, i) => <div key={i}>{line}</div>)}
      </div>
    </div>
  );
};

const TextNode = ({ data }) => {
  return (
    <div className="pu-text-node">
      {data.text && data.text.split('\n').map((line, i) => <div key={i}>{line}</div>)}
    </div>
  );
};

const getInitialNodes = (t) => [
  {
    id: "medika-card",
    type: "creditCard",
    position: { x: 50, y: 100 },
    data: { variant: "green", title: t("payment_utilities.medika_card"), subtitle: "Medika Care", bottomText: t("payment_utilities.medika_link") },
    draggable: false,
  },
  {
    id: "one-card",
    type: "creditCard",
    position: { x: 450, y: 100 },
    data: { variant: "blue", title: "MEDIKA Standard", subtitle: "BICO", bottomText: t("payment_utilities.one_card"), isCenter: true },
    draggable: false,
  },
  {
    id: "bank-card",
    type: "creditCard",
    position: { x: 850, y: 100 },
    data: { variant: "gold", title: "Premium Gold", subtitle: "VietinBank", bottomText: t("payment_utilities.bank_card") },
    draggable: false,
  },
  {
    id: "user",
    type: "iconNode",
    position: { x: 510, y: 395 },
    data: { icon: "user", label: t("payment_utilities.user") },
    draggable: false,
  },
  {
    id: "wallet",
    type: "iconNode",
    position: { x: 200, y: 550 },
    data: { icon: "wallet", label: t("payment_utilities.wallet") },
    draggable: false,
  },
  {
    id: "points",
    type: "iconNode",
    position: { x: 840, y: 550 },
    data: { icon: "points", label: t("payment_utilities.points") },
    draggable: false,
  },
  {
    id: "lbl-balance",
    type: "textNode",
    position: { x: 260, y: 350 },
    data: { text: t("payment_utilities.balance_management") },
    draggable: false,
  },
  {
    id: "lbl-id",
    type: "textNode",
    position: { x: 740, y: 350 },
    data: { text: t("payment_utilities.id_management") },
    draggable: false,
  },
  {
    id: "lbl-trans",
    type: "textNode",
    position: { x: 500, y: 620 },
    data: { text: t("payment_utilities.point_accumulation").replace(/\n/g, ' ') },
    draggable: false,
  }
];

const getInitialEdges = (t) => {
  const edgeStyle = { stroke: "#3b86c4", strokeWidth: 2 };
  const marker = { type: MarkerType.ArrowClosed, color: "#3b86c4", width: 16, height: 16 };

  return [
    { id: "e-medika-one", source: "medika-card", target: "one-card", type: "straight", markerStart: marker, markerEnd: marker, style: edgeStyle, sourceHandle: "right", targetHandle: "left" },
    { id: "e-one-bank", source: "one-card", target: "bank-card", type: "straight", markerStart: marker, markerEnd: marker, style: edgeStyle, sourceHandle: "right", targetHandle: "left" },
    { id: "e-one-wallet", source: "one-card", target: "wallet", type: "straight", markerStart: marker, markerEnd: marker, style: edgeStyle, sourceHandle: "bottom", targetHandle: "top" },
    { id: "e-one-points", source: "one-card", target: "points", type: "straight", markerStart: marker, markerEnd: marker, style: edgeStyle, sourceHandle: "bottom", targetHandle: "top" },
    { id: "e-wallet-points", source: "wallet", target: "points", type: "straight", markerStart: marker, markerEnd: marker, style: edgeStyle, sourceHandle: "right", targetHandle: "left" }
  ];
};

// --- COMPONENT ---

const PaymentUtilities = () => {
  const { t } = useTranslation();

  const nodeTypes = useMemo(() => ({ creditCard: CreditCardNode, iconNode: IconNode, textNode: TextNode }), []);
  
  const translatedNodes = useMemo(() => getInitialNodes(t), [t]);
  const translatedEdges = useMemo(() => getInitialEdges(t), [t]);

  const [nodes, setNodes, onNodesChange] = useNodesState(translatedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(translatedEdges);

  useEffect(() => {
    setNodes(translatedNodes);
    setEdges(translatedEdges);
  }, [translatedNodes, translatedEdges, setNodes, setEdges]);

  return (
    <div className="pu-container">
      <div className="pu-header">
        <h2>{t("payment_utilities.title")}</h2>
      </div>

      <div className="pu-flow-wrapper">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.5}
          maxZoom={2}
          attributionPosition="bottom-right"
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background color="#cbd5e0" gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
};

export default PaymentUtilities;
